import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import { ModuloCatalogoItem } from '../../config/modulosCatalogo';
import { ModuloMasterDto } from '../../services/modulosService';
import { isSuperAdminRole } from '../../utils/sistemaAuthz';
import ModuloLogo from './ModuloLogo';
import ModuloAdminSection from './ModuloAdminSection';
import ModuloMaterialDescargas from './ModuloMaterialDescargas';
import { formatCostoMensual } from './moduloFormat';

interface ModuloDetalleDialogProps {
  open: boolean;
  item: ModuloCatalogoItem | null;
  habilitado: boolean;
  master?: ModuloMasterDto;
  roles?: string[];
  toggling?: boolean;
  onClose: () => void;
  onToggle: (habilitado: boolean) => void;
  onMasterChange: (next: ModuloMasterDto) => void;
}

const ModuloDetalleDialog: React.FC<ModuloDetalleDialogProps> = ({
  open,
  item,
  habilitado,
  master,
  roles,
  toggling,
  onClose,
  onToggle,
  onMasterChange,
}) => {
  const [confirmDisable, setConfirmDisable] = useState(false);
  const isSuperAdmin = isSuperAdminRole(roles);

  if (!item) return null;

  const handleToggleClick = () => {
    if (habilitado) {
      setConfirmDisable(true);
      return;
    }
    onToggle(true);
  };

  const handleConfirmDisable = () => {
    setConfirmDisable(false);
    onToggle(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <ModuloLogo
              campo={item.campo}
              tieneLogo={Boolean(master?.tieneLogo)}
              logoVersion={master?.logoVersion}
              icon={item.icon}
              color={item.color}
              size={64}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" component="div" sx={{ fontWeight: 700 }}>
                {item.nombre}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                <Chip
                  size="small"
                  icon={habilitado ? <CheckCircleIcon /> : undefined}
                  label={habilitado ? 'Habilitado' : 'Deshabilitado'}
                  color={habilitado ? 'success' : 'default'}
                  variant={habilitado ? 'filled' : 'outlined'}
                />
                {isSuperAdmin && (
                  <Typography variant="body2" color="text.secondary">
                    {formatCostoMensual(master?.costoMensual ?? null, master?.moneda ?? null)}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {item.descripcionLarga}
          </Typography>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Procesos principales
              </Typography>
              <List dense disablePadding>
                {item.procesos.map((proceso) => (
                  <ListItem key={proceso} disableGutters sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CircleIcon sx={{ fontSize: 8, color: item.color }} />
                    </ListItemIcon>
                    <ListItemText primary={proceso} />
                  </ListItem>
                ))}
              </List>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Entidades principales
              </Typography>
              <List dense disablePadding>
                {item.entidades.map((entidad) => (
                  <ListItem key={entidad} disableGutters sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CircleIcon sx={{ fontSize: 8, color: item.color }} />
                    </ListItemIcon>
                    <ListItemText primary={entidad} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Stack>

          {isSuperAdmin && (
            <>
              <Divider sx={{ my: 2 }} />
              <ModuloAdminSection
                campo={item.campo}
                icon={item.icon}
                color={item.color}
                master={master}
                onMasterChange={onMasterChange}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <ModuloMaterialDescargas campo={item.campo} />
          </Box>
          <Button onClick={onClose} color="inherit">
            Cerrar
          </Button>
          <Button
            variant="contained"
            color={habilitado ? 'inherit' : 'success'}
            disabled={toggling}
            onClick={handleToggleClick}
          >
            {habilitado ? 'Deshabilitar módulo' : 'Habilitar módulo'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDisable} onClose={() => setConfirmDisable(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Deshabilitar {item.nombre}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Al deshabilitar este módulo se ocultarán sus pantallas del menú y quedarán inactivos los
            permisos asociados. ¿Continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDisable(false)} color="inherit">
            Cancelar
          </Button>
          <Button variant="contained" color="warning" onClick={handleConfirmDisable} disabled={toggling}>
            Deshabilitar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModuloDetalleDialog;
